import React, { useRef, useState } from "react";
import { FaArrowLeft, FaUpload } from "react-icons/fa";
import { myCustomTheme } from "../../Theme";
import "./style/CheckoutPage.css";

const FIREBASE_SHIPPING_API =
    "https://YOUR_REGION-YOUR_PROJECT_ID.cloudfunctions.net/getShadowfaxShippingRate";

const CheckoutPage = ({
    selectedProduct,
    userDetails,
    setUserDetails,
    customerDesign,
    setCustomerDesign,
    onBack,
    onContinue,
    showToast
}) => {
    const fileInputRef = useRef(null);
    const [shippingLoading, setShippingLoading] = useState(false);

    const cartItems = Array.isArray(selectedProduct)
        ? selectedProduct
        : selectedProduct?.cartItems
            ? selectedProduct.cartItems
            : selectedProduct
                ? [selectedProduct]
                : [];

    const cartImages = selectedProduct?.cartImages || {};
    const isBuyAll = cartItems.length > 1;

    const handleClearDesign = () => {
        setCustomerDesign(null);
        if (fileInputRef.current) fileInputRef.current.value = "";
    };

    const getProductImage = (item) => {
        return cartImages[item.id] || item.images?.[0] || customerDesign;
    };

    const fetchShipmentCharge = async (pincode) => {
        if (pincode.length !== 6) return;

        if (pincode === "394107") {
            setUserDetails((prev) => ({
                ...prev,
                pincode,
                shipmentCharge: 50,
                parcelCharge: 50
            }));
            return;
        }

        try {
            setShippingLoading(true);

            const response = await fetch(FIREBASE_SHIPPING_API, {
                method: "POST",
                headers: {
                    "Content-Type": "application/json"
                },
                body: JSON.stringify({
                    pickupPincode: "394520",
                    deliveryPincode: pincode,
                    weight: 0.5,
                    paymentMode: "COD",
                    orderValue: 500
                })
            });

            const data = await response.json();

            if (!response.ok || !data.success) {
                showToast("Shipping charge not found.");
                setUserDetails((prev) => ({
                    ...prev,
                    pincode,
                    shipmentCharge: 0,
                    parcelCharge: 0
                }));
                return;
            }

            const charge = Number(data.shippingCharge || 0);

            setUserDetails((prev) => ({
                ...prev,
                pincode,
                shipmentCharge: charge,
                parcelCharge: charge
            }));
        } catch (error) {
            showToast("Shipping API error.");
            setUserDetails((prev) => ({
                ...prev,
                pincode,
                shipmentCharge: 0,
                parcelCharge: 0
            }));
        } finally {
            setShippingLoading(false);
        }
    };

    const handlePincodeChange = (e) => {
        const pincode = e.target.value.replace(/\D/g, "").slice(0, 6);

        setUserDetails({
            ...userDetails,
            pincode,
            shipmentCharge: 0,
            parcelCharge: 0
        });

        if (pincode.length === 6) {
            fetchShipmentCharge(pincode);
        }
    };

    return (
        <div
            className="checkout-page"
            style={{
                backgroundColor: myCustomTheme.colors.header_footer_bg,
                boxShadow: myCustomTheme.shadows.soft,
                borderRadius: "24px",
                border: `1px solid ${myCustomTheme.colors.high_light}`
            }}
        >
            <button
                className="back-btn"
                onClick={onBack}
                style={{
                    backgroundColor: myCustomTheme.colors.button_bg,
                    color: myCustomTheme.colors.button_text
                }}
            >
                <FaArrowLeft /> Back
            </button>

            <h1 style={{ color: myCustomTheme.colors.heading }}>
                Personal Delivery Details
            </h1>

            {cartItems.length > 0 && (
                <div
                    style={{
                        backgroundColor: myCustomTheme.colors.bg_primary,
                        border: `1px solid ${myCustomTheme.colors.high_light}`,
                        borderRadius: "16px",
                        padding: "15px",
                        marginBottom: "20px"
                    }}
                >
                    <h3 style={{ marginTop: 0, color: myCustomTheme.colors.heading }}>
                        {isBuyAll ? "Cart Items" : "Selected Product"}
                    </h3>

                    {cartItems.map((item) => (
                        <div
                            key={item.id}
                            style={{
                                display: "flex",
                                gap: "12px",
                                alignItems: "center",
                                padding: "10px 0",
                                borderBottom: `1px solid ${myCustomTheme.colors.high_light}`
                            }}
                        >
                            <img
                                src={getProductImage(item)}
                                alt={item.name}
                                style={{
                                    width: "70px",
                                    height: "70px",
                                    objectFit: "cover",
                                    borderRadius: "10px"
                                }}
                            />

                            <div>
                                <h4 style={{ margin: "0 0 5px", color: myCustomTheme.colors.heading }}>
                                    {item.name}
                                </h4>

                                <p style={{ margin: 0, color: myCustomTheme.colors.menu_text }}>
                                    Type: {item.type}
                                </p>

                                {item.type === "Customize" && cartImages[item.id] && (
                                    <p style={{ margin: "4px 0 0", color: "#15803d", fontWeight: "700" }}>
                                        Custom design added
                                    </p>
                                )}
                            </div>
                        </div>
                    ))}
                </div>
            )}

            <div className="checkout-form">
                <div className="form-group">
                    <label className="input-label">Full Name</label>
                    <input
                        type="text"
                        placeholder="Enter your full name"
                        value={userDetails.name || ""}
                        onChange={(e) => setUserDetails({ ...userDetails, name: e.target.value })}
                    />
                </div>

                {!isBuyAll && selectedProduct?.type === "Customize" && (
                    <div
                        className="customization-section"
                        style={{
                            backgroundColor: myCustomTheme.colors.header_footer_bg,
                            border: `1px solid ${myCustomTheme.colors.high_light}`,
                            padding: "20px",
                            borderRadius: "16px",
                            margin: "20px 0"
                        }}
                    >
                        <label className="input-label" style={{ display: "block", marginBottom: "10px" }}>
                            Upload Custom Design
                        </label>

                        {!customerDesign ? (
                            <div className="file-upload-dropzone">
                                <input
                                    ref={fileInputRef}
                                    type="file"
                                    id="design-upload"
                                    accept="image/*"
                                    onChange={(e) => {
                                        const file = e.target.files[0];
                                        if (file) {
                                            const reader = new FileReader();
                                            reader.onloadend = () => setCustomerDesign(reader.result);
                                            reader.readAsDataURL(file);
                                        }
                                    }}
                                    style={{ display: "none" }}
                                />

                                <label htmlFor="design-upload" className="file-upload-label">
                                    <FaUpload style={{ marginRight: "10px" }} /> Upload Image
                                </label>
                            </div>
                        ) : (
                            <div className="image-previews-tray">
                                <div className="preview-thumb-wrapper" style={{ position: "relative", width: "80px" }}>
                                    <img
                                        src={customerDesign}
                                        alt="Design"
                                        className="admin-form-thumb"
                                        style={{ width: "80px", height: "80px", borderRadius: "8px" }}
                                    />

                                    <button type="button" className="thumb-remove-btn" onClick={handleClearDesign}>
                                        ×
                                    </button>
                                </div>
                            </div>
                        )}
                    </div>
                )}

                <div className="form-group">
                    <label className="input-label">Email Address</label>
                    <input
                        type="email"
                        placeholder="name@example.com"
                        value={userDetails.email || ""}
                        onChange={(e) => setUserDetails({ ...userDetails, email: e.target.value })}
                    />
                </div>

                <div className="form-group">
                    <label className="input-label">Mobile Number</label>
                    <input
                        type="text"
                        placeholder="10-digit phone number"
                        value={userDetails.phone || ""}
                        onChange={(e) =>
                            setUserDetails({
                                ...userDetails,
                                phone: e.target.value.replace(/\D/g, "").slice(0, 10)
                            })
                        }
                    />
                </div>

                <div className="form-group">
                    <label className="input-label">Full Delivery Address</label>
                    <textarea
                        placeholder="House No., Street Name, Area, Landmark"
                        value={userDetails.address || ""}
                        onChange={(e) => setUserDetails({ ...userDetails, address: e.target.value })}
                    />
                </div>

                <div className="form-group">
                    <label className="input-label">City</label>
                    <input
                        type="text"
                        placeholder="Enter city"
                        value={userDetails.city || ""}
                        onChange={(e) => setUserDetails({ ...userDetails, city: e.target.value })}
                    />
                </div>

                <div className="form-group">
                    <label className="input-label">State</label>
                    <input
                        type="text"
                        placeholder="Enter state"
                        value={userDetails.state || ""}
                        onChange={(e) => setUserDetails({ ...userDetails, state: e.target.value })}
                    />
                </div>

                <div className="form-group">
                    <label className="input-label">Pincode</label>
                    <input
                        type="text"
                        placeholder="Enter 6-digit pincode"
                        value={userDetails.pincode || ""}
                        onChange={handlePincodeChange}
                    />
                </div>

                <button
                    className="continue-btn"
                    disabled={shippingLoading}
                    onClick={() => {
                        if (
                            !userDetails.name ||
                            !userDetails.email ||
                            !userDetails.phone ||
                            !userDetails.address ||
                            !userDetails.city ||
                            !userDetails.state ||
                            !userDetails.pincode
                        ) {
                            showToast("Please complete all required fields.");
                            return;
                        }

                        if (userDetails.phone.length !== 10) {
                            showToast("Please enter valid 10-digit mobile number.");
                            return;
                        }

                        if (userDetails.pincode.length !== 6) {
                            showToast("Please enter valid 6-digit pincode.");
                            return;
                        }

                        if (shippingLoading) {
                            showToast("Please wait, shipping charge loading.");
                            return;
                        }

                        setUserDetails({
                            ...userDetails,
                            fullAddress: `${userDetails.address}, ${userDetails.city}, ${userDetails.state} - ${userDetails.pincode}`
                        });

                        onContinue();
                    }}
                >
                    {shippingLoading ? "Checking Shipping..." : "Continue To Summary"}
                </button>
            </div>
        </div>
    );
};

export default CheckoutPage;